import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiMail, FiMapPin, FiSend, FiUser, FiFileText } from "react-icons/fi";
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';

const Eyebrow = ({ children }) => {
  const commentText = "//";
  return (
    <div className="eyebrow" aria-hidden>
      <span>{commentText}</span> {children}
    </div>
  );
};

function ContactSection() {
  const { t } = useTranslation();
  const form = useRef();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [formStatus, setFormStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // EmailJS configuration from config file
  const { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY } = EMAILJS_CONFIG;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormStatus("");

    try {
      // Send email using EmailJS
      const result = await emailjs.sendForm(
        SERVICE_ID,
        TEMPLATE_ID,
        form.current,
        PUBLIC_KEY
      );

      console.log('Email sent successfully:', result.text);
      setFormStatus("success");
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: "", email: "", subject: "", message: "" });
        setFormStatus("");
      }, 3000);
    } catch (error) {
      console.error('Error sending email:', error);
      setFormStatus("error");
      
      // Reset error status after 5 seconds
      setTimeout(() => {
        setFormStatus("");
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="card contact">
      <div className="contact__header">
        <Eyebrow>{t('contact.eyebrow')}</Eyebrow>
        <h3 className="contact__title">{t('contact.title')}</h3>
      </div>

      <form ref={form} className="contact__form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              <FiUser />
              <span>{t('contact.form.name')}*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-input"
              placeholder={t('contact.form.namePlaceholder')}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              <FiMail />
              <span>{t('contact.form.email')}*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder={t('contact.form.emailPlaceholder')}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="subject" className="form-label">
            <FiFileText />
            <span>{t('contact.form.subject')}*</span>
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            className="form-input"
            placeholder={t('contact.form.subjectPlaceholder')}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="message" className="form-label">
            <FiMail />
            <span>{t('contact.form.message')}*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="form-textarea"
            placeholder={t('contact.form.messagePlaceholder')}
            rows="6"
            required
          />
        </div>

        <button 
          type="submit" 
          className={`btn btn--primary btn--submit ${formStatus === "success" ? "btn--success" : ""} ${formStatus === "error" ? "btn--error" : ""}`}
          disabled={formStatus === "success" || isLoading}
        >
          <FiSend />
          <span>
            {isLoading ? t('contact.form.sending') : 
             formStatus === "success" ? t('contact.form.sent') : 
             formStatus === "error" ? t('contact.form.error') : 
             t('contact.form.submit')}
          </span>
        </button>

        {formStatus === "success" && (
          <div className="form-success">
            {t('contact.success')}
          </div>
        )}

        {formStatus === "error" && (
          <div className="form-error">
            {t('contact.error')}
          </div>
        )}
      </form>

      <div className="contact__info">
        <div className="contact-info-item">
          <FiMail />
          <span>{t('contact.info.email')}</span>
        </div>
        <div className="contact-info-item">
          <FiMapPin />
          <span>{t('contact.info.location')}</span>
        </div>
      </div>
    </section>
  );
}

export default ContactSection;
